import React from 'react';

import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface IDateControllerInputsProps {
    className?: string;
    name: string;
    value: Date | null;
    onBlur: () => void;
    onChange: (value: string | Date | null) => void;
    invalid: boolean;
    inputRef: React.Ref<HTMLSelectElement>;
    firstYear: number;
    lastYearFromNow: number;
    size?: 'sm' | 'lg';
    disabled?: boolean;
}

interface IDateControllerInputValue {
    year: number | null;
    month: number | null;
    day: number | null;
}

export const DateControllerInputs = (
    {
        className,
        name,
        onChange,
        onBlur,
        value,
        invalid,
        inputRef,
        firstYear,
        lastYearFromNow,
        size,
        disabled,
    }: IDateControllerInputsProps
) => {
    const {t} = useTranslation();
    const [currentDate, setCurrentDate] = React.useState<IDateControllerInputValue>({
        year: value ? value.getFullYear() : null,
        month: value ? value.getMonth() + 1 : null,
        day: value ? value.getDate() : null,
    });

    // define a list of available days based on the current selected month
    const availableDays: number[] = React.useMemo(() => {
        const daysInMonth = (currentDate.year && currentDate.month)
            ? new Date(currentDate.year, currentDate.month, 0).getDate()
            : 31;
        const result = [];
        for (let i = 1; i < daysInMonth + 1; i++) {
            result.push(i);
        }
        return result;
    }, [currentDate]);

    // define the options list for the day selector
    const dayOptions = React.useMemo(() => {
        return availableDays.map(dayNumber => (
            <option value={dayNumber} key={dayNumber}>{dayNumber}</option>
        ));
    }, [availableDays]);

    // define the available year options on mount. The range starts from 1950 and end 32 years in the future
    const yearOptions = React.useMemo(() => {
        const currentYear = new Date().getFullYear();
        const endYear = currentYear + lastYearFromNow;
        const availableYears = [];
        for (let i = endYear; i >= firstYear; i--) {
            availableYears.push(
                <option value={i} key={i}>{i}</option>
            );
        }
        return availableYears;
    }, [firstYear, lastYearFromNow]);

    // whenever `availableDays` changes make sure that the selected day is still valid for that month. If not, clear it
    // to force the user to re-enter
    React.useEffect(() => {
        if (currentDate.day && availableDays.findIndex(availableDay => availableDay === currentDate.day) === -1) {
            setCurrentDate({...currentDate, day: null});
        }
    }, [availableDays, currentDate]);

    // trigger `onChange` whenever the selected date changes. Set to null if it's not valid, for example not all fields
    // have been selected.
    React.useEffect(() => {
        try {
            const newValue = (currentDate.year && currentDate.month && currentDate.day)
                ? new Date(currentDate.year, currentDate.month, currentDate.day)
                : null;
            onChange(newValue);
        } catch {
            onChange(null);
        }
    }, [currentDate, onChange]);

    const monthOptions = React.useMemo(() => (
        <>
            <option value={1}>{t('dates.months.1', 'January')}</option>
            <option value={2}>{t('dates.months.2', 'February')}</option>
            <option value={3}>{t('dates.months.3', 'March')}</option>
            <option value={4}>{t('dates.months.4', 'April')}</option>
            <option value={5}>{t('dates.months.5', 'May')}</option>
            <option value={6}>{t('dates.months.6', 'June')}</option>
            <option value={7}>{t('dates.months.7', 'July')}</option>
            <option value={8}>{t('dates.months.8', 'August')}</option>
            <option value={9}>{t('dates.months.9', 'September')}</option>
            <option value={10}>{t('dates.months.10', 'October')}</option>
            <option value={11}>{t('dates.months.11', 'November')}</option>
            <option value={12}>{t('dates.months.12', 'December')}</option>
        </>
    ), [t]);

    return (
        <Row>
            <Col>
                <Form.Control
                    custom
                    as="select"
                    className={className}
                    name={`${name}.month`}
                    onChange={(e) => setCurrentDate({...currentDate, month: +e.target.value})}
                    defaultValue={value ? value.getMonth() + 1 : ''}
                    isInvalid={invalid}
                    onBlur={onBlur}
                    size={size}
                    disabled={disabled}
                >
                    <option value="">
                        {t('common.month', 'Month')}
                    </option>
                    {monthOptions}
                </Form.Control>
            </Col>
            <Col>
                <Form.Control
                    custom
                    as="select"
                    className={className}
                    name={`${name}.day`}
                    onChange={(e) => setCurrentDate({...currentDate, day: +e.target.value})}
                    defaultValue={value ? value.getDate() : ''}
                    isInvalid={invalid}
                    onBlur={onBlur}
                    ref={inputRef}
                    size={size}
                    disabled={disabled}
                >
                    <option value="">
                        {t('common.day', 'Day')}
                    </option>
                    {dayOptions}
                </Form.Control>
            </Col>
            <Col>
                <Form.Control
                    custom
                    as="select"
                    className={className}
                    name={`${name}.year`}
                    onChange={(e) => setCurrentDate({...currentDate, year: +e.target.value })}
                    defaultValue={value ? value.getFullYear() : ''}
                    isInvalid={invalid}
                    onBlur={onBlur}
                    size={size}
                    disabled={disabled}
                >
                    <option value="">
                        {t('common.year', 'Year')}
                    </option>
                    {yearOptions}
                </Form.Control>
            </Col>
        </Row>
    );
};

DateControllerInputs.defaultProps = {
    firstYear: 1950,
    lastYearFromNow: 32,
};
